import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { LifeBuoy, MessageSquare, Paperclip, Plus, Search, Settings2 } from "lucide-react";
import NewRequestDialog from "../components/help-desk/NewRequestDialog";
import RequestDetail from "../components/help-desk/RequestDetail";
import { CATEGORY, StatusPill, fullTime, ago } from "../components/help-desk/helpDesk";

// Tabs are status buckets. Approvers land on their inbox; everyone else on
// their own requests.
const TABS = {
  inbox: { label: "Inbox", statuses: ["new", "needs_info"] },
  active: { label: "In progress", statuses: ["approved", "claude", "review", "shipped"] },
  mine: { label: "Mine", statuses: null },
  open: { label: "All open", statuses: ["new", "needs_info", "approved", "claude", "review", "shipped"] },
  closed: { label: "Done", statuses: ["done", "declined"] },
};

const URGENCY_RANK = { urgent: 0, high: 1, normal: 2, low: 3 };

function ApproversDialog({ open, onOpenChange, emails, onSaved }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setText((emails || []).join("\n")); }, [open, emails]);

  const save = async () => {
    setSaving(true);
    try {
      const list = text.split(/[\s,]+/).map((e) => e.trim()).filter(Boolean);
      await apiClient.put("/help-desk/settings", { approver_emails: list });
      toast.success("Approvers updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="font-light">Help Desk approvers</DialogTitle>
          <DialogDescription>
            These people get every new request, can approve or decline, and can send work to Claude. One email per line.
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-[13px]" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HelpDesk() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [people, setPeople] = useState([]);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(null);
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [approversOpen, setApproversOpen] = useState(false);

  const selectedId = params.get("request");

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/help-desk/settings");
      setSettings(data);
      setTab((t) => t || (data.is_approver ? "inbox" : "mine"));
      if (data.is_approver) {
        apiClient.get("/help-desk/people").then((r) => setPeople(r.data || [])).catch(() => {});
      }
    } catch {
      setSettings({ is_approver: false });
      setTab((t) => t || "mine");
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/help-desk/requests");
      setRows(data.requests || []);
      setCounts(data.counts || {});
    } catch {
      toast.error("Couldn't load requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); load(); }, [loadSettings, load]);

  useEffect(() => {
    if (params.get("new") === "1") {
      setNewOpen(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const tabs = settings?.is_approver ? ["inbox", "active", "mine", "closed"] : ["mine", "open", "closed"];

  const tabCount = (key) => {
    if (key === "mine") return rows.filter((r) => (r.requester?.id === user?.id || r.assignee?.id === user?.id) && !["done", "declined"].includes(r.status)).length;
    return (TABS[key].statuses || []).reduce((n, s) => n + (counts[s] || 0), 0);
  };

  const visible = useMemo(() => {
    if (!tab) return [];
    let list = rows;
    if (tab === "mine") list = list.filter((r) => r.requester?.id === user?.id || r.assignee?.id === user?.id);
    else list = list.filter((r) => TABS[tab].statuses.includes(r.status));
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((r) => `${r.ref} ${r.title} ${r.requester?.name || ""} ${r.assignee?.name || ""}`.toLowerCase().includes(needle));
    }
    if (tab === "inbox") {
      // Urgent first, then oldest first — the inbox is a queue.
      list = [...list].sort((a, b) => (URGENCY_RANK[a.urgency] ?? 2) - (URGENCY_RANK[b.urgency] ?? 2)
        || new Date(a.created_at) - new Date(b.created_at));
    }
    return list;
  }, [rows, tab, q, user]);

  const openRequest = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("request", id); else next.delete("request");
    setParams(next);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="help-desk-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] sm:text-[32px] font-light tracking-tight flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-muted-foreground" /> Help Desk
          </h1>
          <p className="text-[14px] text-muted-foreground mt-1 max-w-xl">
            Ask HQ for a fix, a feature, access or a change. Every request is timestamped and you can follow it until it's checked off.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {settings?.is_approver && (
            <Button variant="ghost" size="sm" onClick={() => setApproversOpen(true)} title="Who approves requests">
              <Settings2 className="w-4 h-4" />
            </Button>
          )}
          <Button onClick={() => setNewOpen(true)} data-testid="help-desk-new">
            <Plus className="w-4 h-4 mr-1.5" /> New request
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 border-b border-border">
        <div className="flex gap-5 overflow-x-auto -mb-px">
          {tabs.map((key) => (
            <button key={key} type="button" onClick={() => setTab(key)} data-testid={`hd-tab-${key}`}
              className={`whitespace-nowrap pb-2.5 text-[14px] border-b transition-colors ${tab === key ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {TABS[key].label}
              {key !== "closed" && tabCount(key) > 0 && <span className="ml-1.5 text-[12px] text-muted-foreground">{tabCount(key)}</span>}
            </button>
          ))}
        </div>
        <div className="relative sm:ml-auto sm:w-64 pb-2">
          <Search className="w-3.5 h-3.5 absolute left-3 top-[11px] text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search requests" className="pl-8 h-9 text-[14px]" />
        </div>
      </div>

      {loading ? (
        <p className="text-[14px] text-muted-foreground py-10 text-center">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="py-16 text-center space-y-3">
          <p className="text-[15px] text-muted-foreground">
            {tab === "inbox" ? "Inbox zero. Nothing is waiting on you." : tab === "mine" ? "You haven't filed anything yet." : "Nothing here."}
          </p>
          {tab === "mine" && <Button variant="outline" onClick={() => setNewOpen(true)}>File your first request</Button>}
        </div>
      ) : (
        <ul className="divide-y divide-border border-y border-border" data-testid="help-desk-list">
          {visible.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => openRequest(r.id)} data-testid={`hd-row-${r.ref}`}
                className={`w-full text-left px-1 sm:px-2 py-3.5 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-4 hover:bg-muted/40 transition-colors ${selectedId === r.id ? "bg-muted/40" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[12px] text-muted-foreground shrink-0">{r.ref}</span>
                    {(r.urgency === "urgent" || r.urgency === "high") && (
                      <span className={`text-[10px] uppercase tracking-[1.4px] ${r.urgency === "urgent" ? "text-terracotta-700" : "text-muted-foreground"}`}>{r.urgency}</span>
                    )}
                  </div>
                  <p className="text-[15px] truncate">{r.title}</p>
                  <p className="text-[12px] text-muted-foreground truncate">
                    {r.requester?.name} · {CATEGORY[r.category] || r.category}
                    {r.assignee?.name ? ` · ${r.assignee.name} owns it` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-[12px] text-muted-foreground">
                  {r.comment_count > 0 && <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{r.comment_count}</span>}
                  {r.attachment_count > 0 && <span className="inline-flex items-center gap-1"><Paperclip className="w-3 h-3" />{r.attachment_count}</span>}
                  <span title={`Filed ${fullTime(r.created_at)} · updated ${fullTime(r.updated_at)}`} className="w-16 text-right">{ago(r.updated_at)}</span>
                  <StatusPill status={r.status} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Sheet open={!!selectedId} onOpenChange={(o) => { if (!o) openRequest(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-[620px] overflow-y-auto">
          <SheetTitle className="sr-only">Request</SheetTitle>
          {selectedId && (
            <RequestDetail key={selectedId} requestId={selectedId} me={user} settings={settings} people={people} onChanged={load} />
          )}
        </SheetContent>
      </Sheet>

      <NewRequestDialog open={newOpen} onOpenChange={setNewOpen} pageUrl={params.get("from") || null}
        onCreated={(r) => { load(); openRequest(r.id); }} />
      <ApproversDialog open={approversOpen} onOpenChange={setApproversOpen} emails={settings?.approver_emails} onSaved={loadSettings} />
    </div>
  );
}
